import type {TFunction} from 'i18next'
import {useEffect, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {PorQue} from '@/components/ui/por-que'
import {InvitarExternoDialog} from '@/components/invitar-externo-dialog'
import {MiCuentaDialog} from '@/components/mi-cuenta-dialog'
import {useAuth} from '@/hooks/platform/use-auth'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {RESPONSABLE_STANDARD, useCreateDesignation} from '@/hooks/sgsst/evaluacion/use-designations'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {useNotaField} from '@/hooks/common/use-nota-field'
import type {DisenoLicencia} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {useSolicitudSst, useSolicitarSst} from '@/hooks/inci/consola/use-solicitud-sst'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

function nombreDe(employee: Employee): string {
  return `${employee.name} ${employee.last_name}`
}

function conLicenciaVerificada(roster: Employee[]): Employee[] {
  return roster.filter((e) => e.status === 'active' && e.licencia_estado === 'verificada')
}

/** El backend manda las categorías que habilitan la banda (Res. 0312/2019 arts. 4/10/17): aquí solo
 * hay pertenencia, sin re-derivar la regla. Sin banda, permisivo como el backend. */
function habilitadosParaLaBanda(verificados: Employee[], diseno: DisenoLicencia | null): Employee[] {
  if (diseno === null) return verificados
  return verificados.filter(
    (e) => e.licencia_categoria !== null && diseno.categorias_habilitadas.includes(e.licencia_categoria)
  )
}

function esSoloYo(personas: Employee[], userId: string | undefined): boolean {
  return personas.length === 1 && personas[0].user_id === userId
}

function detalleInsuficiente(t: TFunction, verificados: Employee[], soyYo: boolean): string {
  const solo = verificados.length === 1 ? verificados[0] : null
  if (solo === null || solo.licencia_categoria === null) return t('sinSst.categoriaInsuficiente.detalleVarios')
  const categoria = t(`licencia.categorias.${solo.licencia_categoria}`)
  if (soyYo) return t('sinSst.categoriaInsuficiente.detalleYo', {categoria})
  return t('sinSst.categoriaInsuficiente.detalleOtro', {nombre: nombreDe(solo), categoria})
}

function tituloAtajo(t: TFunction, candidatos: Employee[], soyYo: boolean): string {
  if (soyYo) return t('sinSst.conLicencia.tituloYo')
  if (candidatos.length === 1) return t('sinSst.conLicencia.tituloOtro', {nombre: nombreDe(candidatos[0])})
  return t('sinSst.conLicencia.tituloVarios', {cantidad: candidatos.length})
}

function ctaAtajo(t: TFunction, candidatos: Employee[], soyYo: boolean): string {
  if (soyYo) return t('sinSst.conLicencia.ctaYo')
  if (candidatos.length === 1) return t('sinSst.conLicencia.ctaOtro', {nombre: candidatos[0].name})
  return t('sinSst.conLicencia.ctaVarios')
}

function validatePersonaElegida(elegido: Employee | null): elegido is Employee {
  return elegido !== null
}

/** U3: vive dentro de la fila 1.1.1 (sin evaluación, en la pila de avisos). U7: su salida es
 * subordinada, nunca el primario de la vista. */
export function SinSstAccion({
  businessId,
  canAssign,
  designado = false,
  roster = [],
  diseno = null,
  conEstado = true,
}: {
  businessId: string
  canAssign: boolean
  /** There is a designated responsable whose licencia is not yet verificada. */
  designado?: boolean
  /** Vacío sin evaluación abierta: el atajo designa contra ella y sin ella el POST responde 404. */
  roster?: Employee[]
  /** `null` = sin clase de riesgo: no se afirma que a nadie le alcance ni le falte. */
  diseno?: DisenoLicencia | null
  /** `false` cuando la fila que lo hospeda ya dice el estado con su chip. */
  conEstado?: boolean
}) {
  const {t} = useTranslation()
  const {user} = useAuth()
  const {can_use_designaciones} = useCapabilities(businessId)
  const {data: solicitud} = useSolicitudSst(businessId, true)
  const [inviting, setInviting] = useState(false)
  const [asking, setAsking] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [designando, setDesignando] = useState(false)

  const pendiente = solicitud?.estado === 'pendiente'
  const atendida = solicitud?.estado === 'atendida'
  const descartada = solicitud?.estado === 'descartada'

  const verificados = designado ? [] : conLicenciaVerificada(roster)
  // Solo quien alcanza la banda: ofrecer a quien no, prometería un camino que el gate rechaza.
  const candidatos = habilitadosParaLaBanda(verificados, diseno)
  const atajo = canAssign && can_use_designaciones && candidatos.length > 0
  const soyYo = esSoloYo(candidatos, user?.id)
  const exigencia = diseno !== null && candidatos.length === 0 && verificados.length > 0 ? diseno : null

  // El chip de la fila 1.1.1 solo dice «Sin responsable designado»; se calla solo si esto diría lo mismo.
  const diceEstado = conEstado || designado || atajo || exigencia !== null

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1.5',
        diceEstado && 'notice-amber w-fit max-w-full rounded-lg px-3 py-1.5'
      )}
      data-testid="sin-sst-accion">
      {diceEstado && (
        <p className="min-w-0 text-xs">
          <span className="font-medium">
            {atajo
              ? tituloAtajo(t, candidatos, soyYo)
              : exigencia
                ? t('sinSst.categoriaInsuficiente.titulo', {
                    minima: t(`licencia.categorias.${exigencia.categoria_minima}`),
                  })
                : t(designado ? 'sinSst.tituloDesignado' : 'sinSst.title')}
          </span>
          {/* Información sobre la empresa, no un juicio sobre la licencia; Kairos no aconseja cómo escalarla. */}
          {exigencia && (
            <span className="mt-0.5 block" data-testid="sin-sst-categoria-insuficiente">
              {detalleInsuficiente(t, verificados, esSoloYo(verificados, user?.id))}
            </span>
          )}
        </p>
      )}
      {canAssign ? (
        // El sello acompaña al CTA, no lo reemplaza: el dueño conserva las otras salidas.
        <div className="flex flex-wrap items-center gap-3">
          {(pendiente || atendida) && (
            <span
              className="text-xs font-medium"
              data-testid={pendiente ? 'sin-sst-solicitud-pendiente' : 'sin-sst-solicitud-atendida'}>
              {t(pendiente ? 'sinSst.solicitudPendiente' : 'sinSst.solicitudAtendida')}
            </span>
          )}
          {atajo ? (
            // `outline`: el primario de la vista vive en el encabezado (U7).
            <>
              <Button
                size="sm"
                variant="outline"
                data-testid="sin-sst-designar-con-licencia"
                onClick={() => setDesignando(true)}>
                {ctaAtajo(t, candidatos, soyYo)}
              </Button>
              <button
                type="button"
                data-testid="sin-sst-otras-opciones"
                onClick={() => setSheetOpen(true)}
                className="text-xs font-medium underline underline-offset-2">
                {t('sinSst.conLicencia.otrasOpciones')}
              </button>
            </>
          ) : conEstado ? (
            <button
              type="button"
              data-testid="sin-sst-cta"
              onClick={() => setSheetOpen(true)}
              className="text-xs font-medium underline underline-offset-2">
              {t('sinSst.cta')}
            </button>
          ) : (
            // Única salida de la fila 1.1.1: forma de botón, pero `outline` (U7).
            <Button size="sm" variant="outline" data-testid="sin-sst-cta" onClick={() => setSheetOpen(true)}>
              {t('sinSst.cta')}
            </Button>
          )}
        </div>
      ) : (
        // Sin poder asignar es, en la práctica, el responsable designado: quien debe cargar la licencia.
        <span className="flex flex-wrap items-center gap-2 text-xs">
          {t('sinSst.soloOwner')}
          <button
            type="button"
            data-testid="sin-sst-subir-mi-licencia"
            onClick={() => setAccountOpen(true)}
            className="font-medium text-primary underline underline-offset-2">
            {t('sinSst.subirMiLicencia')}
          </button>
        </span>
      )}

      <ResponsableSheet
        businessId={businessId}
        designado={designado}
        canAssign={canAssign}
        pendiente={pendiente}
        atendida={atendida}
        descartada={descartada}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onInvitar={() => {
          setSheetOpen(false)
          setInviting(true)
        }}
        onAsking={() => {
          setSheetOpen(false)
          setAsking(true)
        }}
        onSubirLicencia={() => {
          setSheetOpen(false)
          setAccountOpen(true)
        }}
      />
      <DesignarConLicenciaDialog
        businessId={businessId}
        candidatos={candidatos}
        soyYo={soyYo}
        solicitudPendiente={pendiente}
        open={designando}
        onOpenChange={setDesignando}
      />
      <InvitarExternoDialog businessId={businessId} open={inviting} onOpenChange={setInviting} />
      <SolicitarKairosDialog businessId={businessId} open={asking} onOpenChange={setAsking} />
      <MiCuentaDialog open={accountOpen} onOpenChange={setAccountOpen} />
    </div>
  )
}

/** Designar es un acto legal (estándar 1.1.1, Res. 0312/2019): el atajo propone y el dueño confirma. */
function DesignarConLicenciaDialog({
  businessId,
  candidatos,
  soyYo,
  solicitudPendiente,
  open,
  onOpenChange,
}: {
  businessId: string
  candidatos: Employee[]
  soyYo: boolean
  solicitudPendiente: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const designar = useCreateDesignation(businessId)
  const unico = candidatos.length === 1 ? candidatos[0] : null
  const [contenedor, setContenedor] = useState<HTMLElement | null>(null)
  const [employeeId, setEmployeeId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const elegido = candidatos.find((e) => e.id === employeeId) ?? null

  // Radix solo reporta los cierres del usuario: una apertura programática reinicia acá.
  useEffect(() => {
    if (!open) return
    setError(null)
    setEmployeeId(unico?.id ?? '')
  }, [open, unico])

  function confirmar(e: FormEvent) {
    e.preventDefault()
    if (!validatePersonaElegida(elegido)) {
      setError(t('sinSst.conLicencia.personaRequerida'))
      return
    }
    setError(null)
    designar.mutate(
      {
        standard_code: RESPONSABLE_STANDARD,
        employee_id: elegido.id,
        party: null,
        rol: null,
        // «Rige desde hoy» lo resuelve el backend con `bogota_today`.
        effective_date: null,
        carta_evidence_id: null,
      },
      {onSuccess: () => onOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="designar-con-licencia-dialog" ref={setContenedor}>
        <DialogHeader>
          <DialogTitle>{t('sinSst.conLicencia.dialogTitulo')}</DialogTitle>
          <DialogDescription>{t('sinSst.conLicencia.dialogCuerpo')}</DialogDescription>
        </DialogHeader>
        <div>
          <PorQue
            label={t('sinSst.conLicencia.dialogPorque')}
            contenedor={contenedor}
            testId="designar-con-licencia-porque"
            contenidoTestId="designar-con-licencia-porque-detalle">
            {t('sinSst.conLicencia.dialogPorqueBody')}
          </PorQue>
        </div>
        <form onSubmit={confirmar} className="space-y-4" noValidate>
          {unico ? (
            <p className="text-sm font-medium" data-testid="designar-con-licencia-persona">
              {t(soyYo ? 'sinSst.conLicencia.dialogPersonaYo' : 'sinSst.conLicencia.dialogPersona', {
                nombre: nombreDe(unico),
              })}
            </p>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="designar-con-licencia-persona">{t('sinSst.conLicencia.dialogElegir')}</Label>
              <EntitySearchSelect
                id="designar-con-licencia-persona"
                items={candidatos}
                value={elegido}
                getKey={(e) => e.id}
                filterText={(e) => `${nombreDe(e)} ${e.cedula}`}
                renderOption={(e) => (
                  <span>
                    {nombreDe(e)} · <span className="font-mono text-xs">{e.cedula}</span>
                  </span>
                )}
                renderTriggerValue={(e) => <span>{nombreDe(e)}</span>}
                placeholder={t('sinSst.conLicencia.dialogElegirPlaceholder')}
                searchPlaceholder={t('sinSst.conLicencia.dialogElegirBuscar')}
                noResultsLabel={t('sinSst.conLicencia.dialogElegirSinResultados')}
                testId="designar-con-licencia-persona"
                onPick={(e) => setEmployeeId(e?.id ?? '')}
              />
            </div>
          )}
          {/* La solicitud es del dueño: se le avisa, pero nadie la cancela por él. */}
          {solicitudPendiente && (
            <p className="text-xs" data-testid="designar-con-licencia-solicitud">
              {t('sinSst.conLicencia.dialogSolicitudPendiente')}
            </p>
          )}
          <FormDialogFooter
            error={error}
            pending={designar.isPending}
            submitLabel={t('sinSst.conLicencia.dialogSubmit')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ResponsableSheet({
  businessId,
  designado,
  canAssign,
  pendiente,
  atendida,
  descartada,
  open,
  onOpenChange,
  onInvitar,
  onAsking,
  onSubirLicencia,
}: {
  businessId: string
  designado: boolean
  canAssign: boolean
  pendiente: boolean
  atendida: boolean
  descartada: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onInvitar: () => void
  onAsking: () => void
  onSubirLicencia: () => void
}) {
  const {t} = useTranslation()
  // Ref de callback: Radix monta el contenido después del primer efecto; con `useRef` el popover iría a
  // `document.body` y el primer Escape cerraría el panel entero.
  const [contenedor, setContenedor] = useState<HTMLElement | null>(null)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="responsable-sheet" ref={setContenedor}>
        <DialogHeader>
          <DialogTitle>{t('sinSst.eyebrow')}</DialogTitle>
          <DialogDescription>{t(designado ? 'sinSst.cuerpoDesignado' : 'sinSst.body')}</DialogDescription>
        </DialogHeader>
        {/* `DialogContent` es una rejilla: sin el `<div>` el disparador se estira a todo el ancho. */}
        <div>
          <PorQue
            label={t('sinSst.porque')}
            contenedor={contenedor}
            testId="sin-sst-porque"
            contenidoTestId="sin-sst-porque-detalle">
            {t('sinSst.porqueBody')}
          </PorQue>
        </div>
        {/* No depende de `designado`: el dueño suele ser el profesional con licencia. */}
        <p className="text-xs">
          {t(designado ? 'sinSst.subirLicenciaHint' : 'sinSst.subirLicenciaHintOwner')}{' '}
          <button
            type="button"
            data-testid="sin-sst-subir-licencia"
            onClick={onSubirLicencia}
            className="font-medium text-primary underline underline-offset-2">
            {t('sinSst.subirLicencia')}
          </button>
        </p>
        {canAssign && (
          <div className="flex flex-col gap-2">
            {(pendiente || atendida) && (
              <p className="text-xs" data-testid="sin-sst-solicitud-estado">
                {t(pendiente ? 'sinSst.solicitudPendienteDetalle' : 'sinSst.solicitudAtendidaDetalle')}
              </p>
            )}
            {descartada && (
              <p className="text-xs" data-testid="sin-sst-solicitud-descartada">
                {t('sinSst.solicitudDescartada')}
              </p>
            )}
            {/* Solo `pendiente` lo esconde: solo ahí el backend rechaza el duplicado (409). */}
            {!pendiente && (
              <Button size="sm" data-testid="sin-sst-solicitar" onClick={onAsking}>
                {t(atendida ? 'sinSst.solicitarKairosDeNuevo' : 'sinSst.solicitarKairos')}
              </Button>
            )}
            {/* KAI-148: la hoja es modal; sin cerrarla, la fila se abre detrás del velo. */}
            <Button asChild size="sm" variant="outline" data-testid="sin-sst-designar">
              <Link to={`/businesses/${businessId}/sgsst#std-1.1.1`} onClick={() => onOpenChange(false)}>
                {t('sinSst.asignarEmpleado')}
              </Link>
            </Button>
            <Button size="sm" variant="outline" data-testid="sin-sst-invitar" onClick={onInvitar}>
              {t('sinSst.invitarExterno')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function SolicitarKairosDialog({
  businessId,
  open,
  onOpenChange,
}: {
  businessId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const solicitar = useSolicitarSst(businessId)
  const notaField = useNotaField()
  const [error, setError] = useState<string | null>(null)

  function close() {
    notaField.reset()
    setError(null)
    solicitar.reset()
    onOpenChange(false)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (notaField.validate()) return
    solicitar.mutate(notaField.nota.trim() || null, {
      onSuccess: close,
      onError: (err) => setError(translateApiError(err)),
    })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="solicitar-sst-dialog">
        <DialogHeader>
          <DialogTitle>{t('sinSst.solicitar.title')}</DialogTitle>
          <DialogDescription>{t('sinSst.solicitar.body')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="solicitar-sst-nota">{t('sinSst.solicitar.notaLabel')}</Label>
            <Textarea
              id="solicitar-sst-nota"
              data-testid="solicitar-sst-nota"
              value={notaField.nota}
              onChange={notaField.onChange}
              onBlur={notaField.onBlur}
              maxLength={notaField.maxLength}
              minRows={2}
              aria-invalid={!!notaField.notaError}
            />
            <FieldError error={notaField.notaError} />
          </div>
          <FormDialogFooter
            error={error}
            pending={solicitar.isPending}
            submitLabel={t('sinSst.solicitar.submit')}
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
